import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatAnchor, MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem,MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin, mergeMap } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import {
    CreateFilterCategoryInput,
    FilterCategory,
    LocalFilterDefinition,
    RemoteFilter,
    Store,
    StorePermission,
} from 'app/shared/graph';
import { StoreLocalesService } from 'app/shared/store-locales/store-locales.service';

import {
    EditFilterCategoryDialogComponent,
    EditFilterCategoryDialogData,
    EditFilterCategoryDialogResult
} from './edit-filter-category-dialog.component';
import { FilterCategoryService } from './filter-category.service';
import { PhotoFilterService } from './photo-filter.service';
import { RemoteFilterService } from './remote-filter.service';

@Component({
    selector: 'admin-store-filter-categories-page.ts',
    templateUrl: './store-filter-categories-page.component.html',
    styleUrls: ['./store-filter-categories-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, RouterLink, MatIcon, MatAnchor, MatIconButton, MatMenuTrigger, MatMenu, MatMenuItem, MatDivider, ErrorComponent, MatButton]
})
export class StoreFilterCategoriesPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly authorizationService = inject(AuthorizationService);
    private readonly route = inject(ActivatedRoute);
    private readonly matDialog = inject(MatDialog);
    private readonly filterCategoryService = inject(FilterCategoryService);
    private readonly photoFilterService = inject(PhotoFilterService);
    private readonly remoteFilterService = inject(RemoteFilterService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly storeLocalesService = inject(StoreLocalesService);

    private authorizationContext: AuthorizationContext;
    private filterBuiltIns: Array<LocalFilterDefinition> = [];
    private remoteFilters: Array<RemoteFilter> = [];
    private store: Store;

    readonly labelTranslations = signal<Record<string, Record<string, string>>>({});
    readonly supportedLocales = signal<string[]>([]);

    public storeId: string;
    public filterCategories: Array<FilterCategory> = [];

    ngOnInit(): void {
        this.registerSubscription(this.authorizationService.authorizationContext().subscribe(authorizationContext => {
            this.authorizationContext = authorizationContext;
        }));

        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;

            let filterCategoriesObservable = this.filterCategoryService.loadStoreFilterCategories(this.storeId);
            let localizedLabelsObservable = filterCategoriesObservable.pipe(mergeMap(x => this.storeLocalesService.getStoreLocalizedLabelsAsRecord(this.storeId, x.filterCategories.map(r => this.getLabelLocalizationKey(r.label)))));
            this.subscribeWithGenericLoadingErrorHandling(forkJoin([
                    filterCategoriesObservable,
                    localizedLabelsObservable,
                    this.photoFilterService.getLocalFiltersDefinitions(),
                    this.remoteFilterService.loadRemoteFilters(this.storeId),
                    this.storeLocalesService.listStoreSupportedLocales(this.storeId),
                ]), ([storeWithFilterCategories, labelTranslations, filterBuiltIns, remoteFilters, supportedLocales]) => {
                    this.store = storeWithFilterCategories;
                    this.filterCategories = storeWithFilterCategories.filterCategories;
                    this.filterBuiltIns = filterBuiltIns;
                    this.remoteFilters = remoteFilters;
                    this.labelTranslations.set(labelTranslations);
                    this.supportedLocales.set(supportedLocales);
                }
            );
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    canManageFilterCategories(): boolean {
        if (!this.store) return false;
        return this.authorizationContext.hasStorePermissions(this.storeId, this.store.subscriptionId, StorePermission.ManageFilterCategories);
    }

    protected getLabelLocalizationKey(categoryLabel: string): string {
        return `shared.filters.categories.${categoryLabel}`;
    }

    addFilterCategory() {
        const dialogRef = this.matDialog.open<EditFilterCategoryDialogComponent, EditFilterCategoryDialogData, EditFilterCategoryDialogResult>(EditFilterCategoryDialogComponent, {
            data: {
                mode: 'create',
                storeId: this.storeId,
                supportedLocales: this.supportedLocales(),
                filterBuiltIn: this.filterBuiltIns,
                remoteFilters: this.remoteFilters,
                filterCategories: this.filterCategories
            } as EditFilterCategoryDialogData
        });
        dialogRef.afterClosed().subscribe(result => {
            if (!result)
                return;

            let newFilterCategory: CreateFilterCategoryInput = {
                label: result.label,
                storeId: this.storeId,
                builtInFilters: result.builtInFilter,
                remoteFilters: result.remoteFilters
            };
            this.subscribeWithGenericSavingErrorHandling(
                forkJoin([
                    this.filterCategoryService.createFilterCategory(newFilterCategory),
                    this.storeLocalesService.updateStoreLocalizedLabel(
                        this.storeId,
                        this.getLabelLocalizationKey(result.label),
                        result.labelTranslations,
                    )
                ])                ,
                ([createdCategory]) => {
                    this.filterCategories.push(createdCategory);
                    this.labelTranslations()[this.getLabelLocalizationKey(createdCategory.label)] = result.labelTranslations;
                });
        });
    }

    openEditFilterCategory(filterCategory: FilterCategory) {
        const dialogRef = this.matDialog.open<EditFilterCategoryDialogComponent, EditFilterCategoryDialogData, EditFilterCategoryDialogResult>(EditFilterCategoryDialogComponent, {
            data: {
                mode: 'edit',
                storeId: this.storeId,
                supportedLocales: this.supportedLocales(),
                labelTranslations: this.labelTranslations()[this.getLabelLocalizationKey(filterCategory.label)],
                filterBuiltIn: this.filterBuiltIns,
                remoteFilters: this.remoteFilters,
                filterCategory,
                filterCategories: this.filterCategories
            } as EditFilterCategoryDialogData
        });
        dialogRef.afterClosed().subscribe(result => {
            if (!result)
                return;

            this.subscribeWithGenericSavingErrorHandling(
                forkJoin([
                    this.storeLocalesService.updateStoreLocalizedLabel(
                        this.storeId,
                        this.getLabelLocalizationKey(result.label),
                        result.labelTranslations,
                    ),
                    this.filterCategoryService.updateFilterCategory({
                        id: result.id!,
                        storeId: this.storeId,
                        label: result.label!,
                        builtInFilters: result.builtInFilter,
                        remoteFilters: result.remoteFilters,
                    }),
                ]),
                () => {
                    filterCategory.label = result.label;
                    filterCategory.builtInFilters = result.builtInFilter;
                    filterCategory.remoteFilters = this.remoteFilters.filter(rf => result.remoteFilters.indexOf(rf.id) !== -1);
                    this.labelTranslations()[this.getLabelLocalizationKey(filterCategory.label)] = result.labelTranslations;
                }
            );
        });
    }

    deleteFilterCategory(filterCategory: FilterCategory) {
        this.confirmDialogService.confirm(
            'Delete Filter Category',
            `You will delete this filter category: ${filterCategory.label}.`,
            undefined,
            {
                yes: {
                    label: 'Delete',
                    warn: true
                }
            }
        ).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(
                this.filterCategoryService.deleteFilterCategory({
                    storeId: this.storeId,
                    filterCategoryId: filterCategory.id
                })
                , () => {
                    let categoryIndex = this.filterCategories.findIndex(f => f.id == filterCategory.id);
                    if (categoryIndex !== -1) {
                        this.filterCategories.splice(categoryIndex, 1)
                    }
                });
        })
    }
}
